import api from './api';
import type { PharmacyFeatureAuditEntry } from './pharmacyFeatures';

/**
 * One block's state. On a GET response every catalogue key is present,
 * normalised, with `isSet` telling the resolver whether this is a stored
 * value or the catalogue default (R1/R2, `resolveBlockHidden` in
 * usePharmacyVisibility.ts). On a PATCH body (see `UpdateVisibilityEntry`)
 * only `hidden`/`hiddenFor` are ever sent, as a full replace — `isSet` is
 * server-computed and never written.
 */
export interface PharmacyVisibilityEntry {
  /** Hidden for everyone at the pharmacy, regardless of role. */
  hidden: boolean;
  /** Roles this block is hidden for (D3). A user with no resolved role is never hidden anything by role. */
  hiddenFor: string[];
  /** GET only: true when this key exists in the stored record (false = the catalogue default). */
  isSet: boolean;
}

export type PharmacyVisibilityRecord = Record<string, PharmacyVisibilityEntry>;

export interface PharmacyVisibilityResponse {
  pharmacyId: string;
  /** Every catalogue key, normalised. */
  blocks: PharmacyVisibilityRecord;
  /** The last 10 visibility audit rows for this pharmacy — same row shape as the features endpoint's recentChanges. */
  recentChanges: PharmacyFeatureAuditEntry[];
}

/**
 * One block's requested state in a PATCH body. A FULL REPLACE of that
 * block's stored entry, not a merge — the backend writes exactly what is
 * sent, so omitting `hidden` (or sending it `undefined`) resets it to
 * `false`, and omitting `hiddenFor` resets it to `[]`. Both fields are
 * therefore required here, not optional: a caller must always send the
 * complete entry it wants stored (Task 5's panel always sends both).
 */
export interface UpdateVisibilityEntry {
  hidden: boolean;
  hiddenFor: string[];
}

/**
 * Body of PATCH /pharmacies/:id/visibility. Only the keys present in `blocks`
 * are written (each as a full replace, see `UpdateVisibilityEntry`); a key
 * absent from `blocks` is left untouched.
 *
 * `null` instead of an entry RESETS that block to its catalogue default
 * (R11): the key leaves the stored record, so the next GET reports
 * `isSet: false` and the resolver falls back to the default.
 */
export interface UpdatePharmacyVisibilityRequest {
  blocks: Record<string, UpdateVisibilityEntry | null>;
}

const baseUrl = '/api/pharmacies';

const pharmacyVisibilityService = {
  async get(pharmacyId: string): Promise<PharmacyVisibilityResponse> {
    const response = await api.get<PharmacyVisibilityResponse>(
      `${baseUrl}/${pharmacyId}/visibility`,
    );
    return response.data;
  },

  async update(
    pharmacyId: string,
    payload: UpdatePharmacyVisibilityRequest,
  ): Promise<PharmacyVisibilityResponse> {
    const response = await api.patch<PharmacyVisibilityResponse>(
      `${baseUrl}/${pharmacyId}/visibility`,
      payload,
    );
    return response.data;
  },
};

export default pharmacyVisibilityService;
